import { getClientsNew } from "api/graphQL_clients";
import { useEffect, useState, useContext, useRef } from "react";
import ClientsTable from "./components/ClientsList";
import CreateClientModal from "./components/CreateClientModal";
import { getSingleRole } from "api/graphQL_role";
import { getAllGatewaysSecond } from "api/graphQL_gateways";
import { getId } from "utils/auth";
import { ClientContext } from "ClientProvider";
import { useLocation, useNavigate } from "react-router-dom";

export interface ClientRowObj {
  organization: string;
  name: string;
  description: string;
  website: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  client_id: string;
  client_secret: string;
  org_id: string;
  org_name: string;
  client_gateway_active?: string;
  created_at: any;
  assigned_payment_methods: any;
}

export type AllGatewayObj = {
  name: string;
  id: string;
};

const Organizations = () => {
  const [clients, setClients] = useState<ClientRowObj[]>([]);
  const [isDataLoading, setIsDataLoading] = useState<boolean>();
  const { singleRoleData, setSingleRoleData } = useContext(ClientContext);
  const [page, setPage] = useState<number>(1);
  const [totalpage, setTotalPage] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [allGateways, setAllGateways] = useState<AllGatewayObj[]>([]);
  const [searchTexts, setSearchTexts] = useState<string>("");
  const location = useLocation();
  const navigate = useNavigate();

  const prevValues = useRef<{ searchTexts: string }>({ searchTexts: "__init__" });

  const fetchClients = async () => {
    let finalSearchText = "";
    if (location.state?.search) {
      finalSearchText = location.state.search;
      prevValues.current = { searchTexts: location.state.search };
      navigate(".", { replace: true, state: null });
    } else {
      finalSearchText = searchTexts;
    }
    if (pageSize === null || pageSize === undefined) { setIsDataLoading(false); return; }
    setIsDataLoading(true);
    getClientsNew({ page, per_page: pageSize, searchTexts: finalSearchText })
      .then((data) => {
        if (data.status === "Error") {
          setClients([]); setTotalPage(1); setTotalItems(0); setCurrentPage(1);
        } else {
          setClients(data?.[0]);
          setTotalPage(data?.[1].total_pages);
          setTotalItems(data?.[1].total_items);
          setCurrentPage(data?.[1].current_page);
        }
      })
      .catch(() => setClients([]))
      .finally(() => setIsDataLoading(false));
  };

  const fetchRole = () => {
    getSingleRole(getId()).then((data) => setSingleRoleData(data ?? [])).catch(() => {});
  };

  const fetchAllGateways = () => {
    getAllGatewaysSecond()
      .then((data) => setAllGateways(data?.[0] ? data : []))
      .catch(() => {});
  };

  useEffect(() => { fetchAllGateways(); }, []);

  const serchboxValueChange = async (searchTerm: string) => {
    const search_txt = searchTerm || "";
    if (search_txt !== searchTexts && searchTexts !== "") {
      await setSearchTexts(search_txt);
      setPage(1); setCurrentPage(1);
    } else {
      await setSearchTexts(search_txt);
    }
  };

  useEffect(() => {
    if (prevValues.current.searchTexts === searchTexts) return;
    fetchClients();
    fetchRole();
  }, [page, searchTexts, pageSize]);

  useEffect(() => { setPageSize(50); }, [searchTexts]);

  return (
    <div className="cl-root">
      {/* Page header */}
      <div className="cl-page-header">
        <div className="cl-page-title-block">
          <div className="cl-page-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div>
            <h1 className="cl-page-heading">Clients</h1>
            <p className="cl-page-sub">Manage your client accounts and gateway assignments</p>
          </div>
        </div>

        <div className="cl-page-actions">
          <CreateClientModal fetchClients={fetchClients} roleData={singleRoleData} allGateways={allGateways} />
        </div>
      </div>

      {/* Table */}
      <ClientsTable
        tableData={clients}
        fetchClients={fetchClients}
        isLoading={isDataLoading}
        roleData={singleRoleData}
        page={page}
        setPage={setPage}
        totalpage={totalpage}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        allGateways={allGateways}
        setPageSize={setPageSize}
        onValueChange={serchboxValueChange}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .cl-root {
          font-family: 'Plus Jakarta Sans', sans-serif;
          min-height: 100vh;
          display: flex; flex-direction: column; gap: 20px;
        }
        .cl-page-header {
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 16px;
        }
        .cl-page-title-block { display: flex; align-items: center; gap: 14px; }
        .cl-page-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #bfdbfe;
          display: flex; align-items: center; justify-content: center;
          color: #2563eb; flex-shrink: 0;
        }
        .cl-page-heading { font-size: 20px; font-weight: 800; color: #0f172a; letter-spacing: -0.4px; margin-bottom: 2px;line-height:22px; }
        .cl-page-sub { font-size: 13px; color: #94a3b8; font-weight: 500; }
        .cl-page-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }

        @media (max-width: 600px) {
          .cl-root { padding: 14px; gap: 14px; }
          .cl-page-header { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
    </div>
  );
};

export default Organizations;